"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import { partnerService, type Partner } from "@/lib/services/partnerService";

export default function PartnersSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await partnerService.getActive();
        setPartners(data);
      } catch (error) {
        console.error('Failed to load partners:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="pt-12 pb-24 bg-white border-t border-border-gray">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="on-scroll text-center mb-14">
          <div className="section-label mx-auto w-fit mb-6">Our Trusted Partners</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
            Built Together, Delivered Better
          </h2>
          <p className="text-body-text max-w-xl mx-auto">
            We&apos;re proud to collaborate with forward-thinking organizations who share our vision. Because great communities are built on great partnerships.
          </p>
        </div>

        {!loading && partners.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {partners?.map((partner) => (
              <a
                key={partner?.id}
                href={partner?.websiteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-6 rounded-3xl border border-border-gray hover:border-brand-purple/30 hover:shadow-purple-sm transition-all duration-300 hover:shadow-lg group"
              >
                <AppImage
                  src={partner?.logoUrl}
                  alt={`${partner?.name} logo`}
                  width={160}
                  height={60}
                  className="partner-logo h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105 [backface-visibility:hidden] [transform-style:preserve-3d]"
                />
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}